import {
	UpdateSecurityDetailsFormDto,
	UpdateSecurityDetailsShape,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/security.zod.ts';
import { updateSecurity } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import {
	DataSecurityWithAllFeaturesAndRanksTct,
	SecurityIssuedIn,
	SecurityType,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SecurityWarningModalProps } from './SecurityWarningModal.types';
import { featuresLosingRequirements } from '@dealstack/shared/lib/common/securities/securityFeatureRules';
import { makeCascadeWarningDescription } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatures.utils.ts';
import { makeSecurityFeatureSnapshot } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityFeatures/securityFeatureSnapshot.ts';

export enum EDIT_SECURITY_DETAILS_FORM_FIELD {
	id = 'id',
	name = 'name',
	type = 'type',
	currency = 'currency',
	nominalValue = 'nominalValue',
	votesPerUnit = 'votesPerUnit',
	issuedIn = 'issuedIn',
	equityBasis = 'equityBasis',
	residueRights = 'residueRights',
	includeInReturns = 'includeInReturns',
	allowFractionalOwnership = 'allowFractionalOwnership',
	unitDenominator = 'unitDenominator',
	isMgtLoan = 'isMgtLoan',
	loanDefinitionId = 'loanDefinitionId',
}

export const useEditSecurityDetailsController = (
	security?: DataSecurityWithAllFeaturesAndRanksTct | null,
	refetchSecurityDetails?: (securityId: string) => void
) => {
	const tCapManage = useTranslationCapManage();
	const [warningModalData, setWarningModalData] = useState<SecurityWarningModalProps | null>(
		null
	);

	const updateSecurityRequest = useMutation({
		mutationFn: updateSecurity,
	});
	const queryClient = useQueryClient();

	const [editMode, setEditMode] = useState(false);

	const editSecurityForm = useForm<UpdateSecurityDetailsFormDto>({
		mode: 'all',
		resolver: zodResolver(UpdateSecurityDetailsShape),
		defaultValues: {
			id: security?.id,
			currency: security?.currency,
			name: security?.name,
			personId: security?.issuerId,
			type: security?.type,
			nominalValue: security?.nominalValue,
			votesPerUnit: security?.votesPerUnit,
			issuedIn: security?.issuedIn,
			equityBasis: security?.equityBasis,
			residueRights: security?.residueRights,
			includeInReturns: security?.includeInReturns ?? true,
			unitDenominator: security?.unitDenominator,
			loanDefinitionId: security?.loanDefinitionId,
			isMgtLoan: !!security?.loanDefinitionId,
		},
		shouldFocusError: false,
	});

	const isIssuedInUnits =
		editSecurityForm?.watch(EDIT_SECURITY_DETAILS_FORM_FIELD.issuedIn) ===
		SecurityIssuedIn.UNITS;

	const allowFractionalOwnership = !!editSecurityForm?.watch(
		EDIT_SECURITY_DETAILS_FORM_FIELD.allowFractionalOwnership
	);

	const residueRights = editSecurityForm?.watch(EDIT_SECURITY_DETAILS_FORM_FIELD.residueRights);
	const noResidueRights = !residueRights;

	const unitDenominator = editSecurityForm?.watch(
		EDIT_SECURITY_DETAILS_FORM_FIELD.unitDenominator
	);

	useEffect(() => {
		if (unitDenominator === 1) {
			return;
		}

		if (!allowFractionalOwnership) {
			editSecurityForm.setValue(EDIT_SECURITY_DETAILS_FORM_FIELD.unitDenominator, null);
		} else {
			editSecurityForm.setValue(
				EDIT_SECURITY_DETAILS_FORM_FIELD.unitDenominator,
				security?.unitDenominator
			);
		}
	}, [allowFractionalOwnership]);

	useEffect(() => {
		editSecurityForm.setValue(
			EDIT_SECURITY_DETAILS_FORM_FIELD.allowFractionalOwnership,
			isIssuedInUnits && security?.unitDenominator !== 1
		);
	}, [isIssuedInUnits]);

	useEffect(() => {
		security &&
			editSecurityForm.reset({
				id: security?.id,
				currency: security?.currency,
				name: security?.name,
				personId: security?.issuerId,
				type: security?.type,
				issuedIn: security?.issuedIn,
				equityBasis: security?.equityBasis,
				residueRights: security?.residueRights,
				includeInReturns: security?.includeInReturns ?? true,
				votesPerUnit: security?.votesPerUnit ?? undefined,
				nominalValue: security?.nominalValue ?? undefined,
				allowFractionalOwnership: security?.unitDenominator > 1,
				unitDenominator: security?.unitDenominator,
				loanDefinitionId: security?.loanDefinitionId,
				isMgtLoan: !!security?.loanDefinitionId,
			});
	}, [security]);

	const onSubmitDetails = (formValues: UpdateSecurityDetailsFormDto) => {
		const { allowFractionalOwnership, unitDenominator, ...rest } = formValues ?? {};

		updateSecurityRequest.mutate(
			{
				...rest,
				unitDenominator: allowFractionalOwnership ? unitDenominator : null,
			},
			{
				onSuccess: () => {
					showToastV2({
						title: tCapManage('toasts.success.security_updated_successfully'),
					});

					if (refetchSecurityDetails) {
						refetchSecurityDetails(formValues.id);
					}

					setEditMode(false);

					void queryClient.invalidateQueries({
						queryKey: [
							formatUnicorn(API_PATHS.AUDIT_ENTITY_ENTITYID_GET, {
								entityId: formValues.id,
							}),
							{ entityId: formValues.id },
						],
					});
				},
			}
		);
	};

	const handleIssuedInChange = (value: SecurityIssuedIn) => {
		const prevIssuedInValue = security?.issuedIn;
		if (!security) {
			return;
		}

		const prev = makeSecurityFeatureSnapshot(security);
		const cascade = featuresLosingRequirements(prev, { ...prev, issuedIn: value });

		if (cascade.features.length || cascade.clearedStates.length) {
			setWarningModalData({
				description: makeCascadeWarningDescription(
					tCapManage,
					cascade.features,
					cascade.clearedStates
				),
				onRevert: () => {
					editSecurityForm.setValue(
						EDIT_SECURITY_DETAILS_FORM_FIELD.issuedIn,
						prevIssuedInValue,
						{
							shouldDirty: true,
							shouldTouch: true,
						}
					);
					setWarningModalData(null);
				},
				onClose: () => setWarningModalData(null),
			});
		}
	};
	const handleResidueRightsChange = (value: string): void => {
		const toggled = value === 'true';
		const prevResidueRights = !!security?.residueRights;
		if (!security) {
			return;
		}

		const prev = makeSecurityFeatureSnapshot(security);
		const cascade = featuresLosingRequirements(prev, {
			...prev,
			residueRights: toggled,
		});

		if (cascade.features.length || cascade.clearedStates.length) {
			setWarningModalData({
				description: makeCascadeWarningDescription(
					tCapManage,
					cascade.features,
					cascade.clearedStates
				),
				onRevert: () => {
					editSecurityForm.setValue(
						EDIT_SECURITY_DETAILS_FORM_FIELD.residueRights,
						prevResidueRights,
						{
							shouldDirty: true,
							shouldTouch: true,
						}
					);
					setWarningModalData(null);
				},
				onClose: () => setWarningModalData(null),
			});
		}
	};

	const disableIsMgtLoanToggle =
		editSecurityForm.watch(EDIT_SECURITY_DETAILS_FORM_FIELD.type) !== SecurityType.LOAN;
	const disableLoanDefinitionSelect = !editSecurityForm.watch(
		EDIT_SECURITY_DETAILS_FORM_FIELD.isMgtLoan
	);

	useEffect(() => {
		if (disableIsMgtLoanToggle) {
			editSecurityForm.setValue(EDIT_SECURITY_DETAILS_FORM_FIELD.isMgtLoan, false);
			editSecurityForm.setValue(EDIT_SECURITY_DETAILS_FORM_FIELD.loanDefinitionId, null);
		}
	}, [disableIsMgtLoanToggle]);

	useEffect(() => {
		if (disableLoanDefinitionSelect) {
			editSecurityForm.setValue(EDIT_SECURITY_DETAILS_FORM_FIELD.loanDefinitionId, null);
		}
	}, [disableLoanDefinitionSelect]);

	return {
		updateSecurityRequest,
		onSubmitDetails,
		editSecurityForm,
		isIssuedInUnits,
		allowFractionalOwnership,
		editMode,
		setEditMode,
		noResidueRights,
		handleResidueRightsChange,
		disableIsMgtLoanToggle,
		disableLoanDefinitionSelect,
		handleIssuedInChange,
		warningModalData,
	};
};
